import { View } from 'react-native';
import Head from 'next/head';
import NextImage, { type StaticImageData } from 'next/image';
import noImage from 'shared/assets/no-image.png';
import { CardMediaPlaceholder } from 'shared/components/card-media-placeholder';
import type { ImageProps } from './shared';
import { webImageSizeTypeDimensions } from './shared';
import { useMemo, useState } from 'react';
import { buildMediaImageSources, getMediaImageVersions } from 'shared/library/media-image-versions';

type WebImageProps = ImageProps<StaticImageData> & {
  onLoad?: () => void;
  onError?: () => void;
};

export const Image = function ({
  src: source,
  imageVersions,
  alt,
  containerClassName,
  imageClassName,
  priority,
  draggable,
  maxSourceWidth,
  loading,
  sizes,
  onClick,
  onLoad,
  onError,
  sizeType,
  width,
  aspectRatio,
}: WebImageProps) {
  const isMediaSource = source && typeof source === 'object' && 'url' in source;
  const src = isMediaSource ? source.url : source;
  const versions = imageVersions ?? (isMediaSource ? source.image_versions : undefined);
  const [failedSource, setFailedSource] = useState<WebImageProps['src']>(null);

  const dimensions = useMemo(() => {
    return sizeType ? webImageSizeTypeDimensions[sizeType] : undefined;
  }, [sizeType]);
  const resolvedAspectRatio = aspectRatio ?? dimensions?.aspectRatio;
  const resolvedSizes = sizes ?? dimensions?.sizes ?? `${width ? width + 'px' : '100vw'}`;
  const mediaSources = useMemo(
    () =>
      src
        ? buildMediaImageSources(
            getMediaImageVersions(versions, {
              sizeType,
              aspectRatio: resolvedAspectRatio,
              maxSourceWidth,
            }),
            width ?? dimensions?.width ?? 1280,
          )
        : null,
    [dimensions?.width, maxSourceWidth, resolvedAspectRatio, sizeType, src, versions, width],
  );
  // Compare URL candidates, not DTO identity: refetching unchanged data must not retry failures.
  const sourceIdentity = mediaSources
    ? `${src}\n${mediaSources.src}\n${mediaSources.srcSet ?? ''}`
    : src;
  const hasLoadError = Boolean(src) && sourceIdentity === failedSource;
  const imageSrc = !src || hasLoadError ? noImage : src;
  const shouldRenderPlaceholder =
    !src || hasLoadError ? sizeType === 'card' || sizeType === 'large' : false;

  function handleError() {
    setFailedSource(sourceIdentity);
    onError?.();
  }

  if (shouldRenderPlaceholder) {
    return <CardMediaPlaceholder className={`${containerClassName} relative`} />;
  }

  if (mediaSources && typeof imageSrc === 'string') {
    // The media worker renders each listed width; srcset descriptors match delivered widths.
    return (
      <View className={`${containerClassName}`}>
        {priority && (
          <Head>
            <link
              key={`media-image-${mediaSources.src}`}
              rel="preload"
              as="image"
              href={mediaSources.srcSet ? undefined : mediaSources.src}
              imageSrcSet={mediaSources.srcSet}
              imageSizes={mediaSources.srcSet ? resolvedSizes : undefined}
              fetchPriority="high"
            />
          </Head>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          sizes={mediaSources.srcSet ? resolvedSizes : undefined}
          srcSet={mediaSources.srcSet}
          src={mediaSources.src}
          alt={alt ?? ''}
          draggable={draggable}
          className={imageClassName}
          onClick={onClick}
          onLoad={onLoad}
          onError={handleError}
          loading={priority ? undefined : (loading ?? 'lazy')}
          fetchPriority={priority ? 'high' : undefined}
          decoding="async"
          style={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
          }}
        />
      </View>
    );
  }

  return (
    <View className={`${containerClassName}`}>
      <NextImage
        src={imageSrc}
        alt={alt ?? ''}
        draggable={draggable}
        className={imageClassName}
        onClick={onClick}
        onLoad={onLoad}
        onError={handleError}
        priority={priority}
        loading={loading}
        fetchPriority={priority ? 'high' : 'auto'}
        fill={true}
        unoptimized={true}
        sizes={resolvedSizes}
      />
    </View>
  );
};
